import { useEffect, useState } from 'react';
import { Lock, Check, Trophy, Goal, MapPin, ChevronDown, Loader2, Clock, Search } from 'lucide-react';
import {
  useWcSavePrediction,
  useWcTeamPlayers,
  useWcScoring,
  type WcMatch,
  type WcPhase,
  type WcScoringConfig,
} from '@/lib/queries';
import { Flag, PHASE_SHORT, formatKickoff, slotLabel, useViewerTimezone } from './common';
import { cn } from '@/lib/cn';

/** Tick compartido por tarjeta para el countdown (actualiza cada 30s). */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** "Cierra en 2h 34m" — sólo dentro de las 48h previas al partido. */
function countdownLabel(kickoffIso: string, now: number): string | null {
  const diff = new Date(kickoffIso).getTime() - now;
  if (diff <= 0) return null;
  const mins = Math.floor(diff / 60_000);
  if (mins >= 2880) return null;
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `Cierra en ${d}d ${h}h`;
  if (h > 0) return `Cierra en ${h}h ${m}m`;
  return `Cierra en ${m}m`;
}

function phaseMult(cfg: WcScoringConfig, phase: WcPhase): number {
  switch (phase) {
    case 'GROUP':
      return cfg.multGroup;
    case 'R32':
      return cfg.multR32;
    case 'R16':
      return cfg.multR16;
    case 'QF':
      return cfg.multQF;
    case 'SF':
      return cfg.multSF;
    default:
      return cfg.multFinal;
  }
}

const PHASE_ES: Record<string, string> = {
  '1H': '1er tiempo',
  '2H': '2do tiempo',
  HT: 'Medio tiempo',
  ET: 'Tiempo extra',
  PEN: 'Penales',
  FT: 'Final',
};
/** Etiqueta del estado en vivo: "1er tiempo · 23'", "Medio tiempo", "Final"… */
function buildLiveLabel(liveStatus: string | null, liveMinute: number | null): string {
  if ((liveMinute != null && liveMinute >= 999) || liveStatus === 'FT') return 'Final';
  const phase = liveStatus ? PHASE_ES[liveStatus] : null;
  const showMin = liveMinute != null && liveMinute > 0 && liveMinute < 999;
  const min = showMin ? `${liveMinute}'` : null;
  // El minuto sólo acompaña a las fases de juego corrido, no a medio tiempo/penales.
  const withMin = phase && min && (liveStatus === '1H' || liveStatus === '2H' || liveStatus === 'ET');
  if (withMin) return `${phase} · ${min}`;
  if (phase) return phase;
  if (min) return `EN VIVO · ${min}`;
  return 'EN VIVO';
}

function Stepper({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(Math.min(20, value + 1))}
        className="flex h-6 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary disabled:opacity-30"
        aria-label="Subir"
      >
        +
      </button>
      <div
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-xl text-2xl font-extrabold tabular-nums',
          disabled ? 'bg-bg text-font-secondary' : 'bg-primary-subtle text-primary',
        )}
      >
        {value}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(Math.max(0, value - 1))}
        className="flex h-6 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary disabled:opacity-30"
        aria-label="Bajar"
      >
        –
      </button>
    </div>
  );
}

/** Normaliza para buscar sin acentos ni mayúsculas. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Selector de goleador (jugadores de ambos equipos, con buscador). */
function ScorerPicker({
  match,
  value,
  onChange,
  fallbackJersey,
}: {
  match: WcMatch;
  value: string | null;
  onChange: (id: string | null) => void;
  fallbackJersey?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const home = useWcTeamPlayers(open ? match.homeTeam?.id : undefined);
  const away = useWcTeamPlayers(open ? match.awayTeam?.id : undefined);

  const allPlayers = [...(home.data?.items ?? []), ...(away.data?.items ?? [])];
  const selected = allPlayers.find((p) => p.id === value);
  // El nombre guardado viene del API aunque los planteles aún no estén cargados
  const label = selected?.jersey ?? fallbackJersey ?? '…';

  if (!match.homeTeam || !match.awayTeam) return null;

  return (
    <div className="mt-3 border-t border-border-light pt-3">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setQ('');
        }}
        className="flex w-full items-center gap-2 text-xs font-semibold text-font-secondary transition hover:text-primary"
      >
        <Goal size={13} className="text-primary" />
        {value ? (
          <span className="text-primary">Goleador: {label}</span>
        ) : (
          'Pronosticar goleador (opcional, +pts)'
        )}
        <ChevronDown size={13} className={cn('ml-auto transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-border bg-white">
          <div className="flex items-center gap-2 border-b border-border-light px-2.5 py-2">
            <Search size={12} className="text-font-tertiary" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar jugador..."
              className="flex-1 bg-transparent text-xs focus:outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto p-1">
            <button
              type="button"
              onClick={() => onChange(null)}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-bg',
                !value && 'bg-primary-subtle font-semibold text-primary',
              )}
            >
              Sin goleador
            </button>
            {[match.homeTeam, match.awayTeam].map((team, i) => {
              const all = (i === 0 ? home : away).data?.items ?? [];
              const loading = (i === 0 ? home : away).isLoading;
              const players = q
                ? all.filter((p) => norm(p.jersey + ' ' + p.name).includes(norm(q)))
                : all;
              if (q && players.length === 0 && !loading) return null;
              return (
                <div key={team!.id}>
                  <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-font-tertiary">
                    <Flag iso2={team!.iso2} className="h-3 w-4" /> {team!.nameEs}
                  </div>
                  {loading && <div className="px-2 py-1 text-xs text-font-tertiary">Cargando…</div>}
                  {players.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => onChange(p.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-bg',
                        value === p.id && 'bg-primary-subtle font-semibold text-primary',
                      )}
                    >
                      <span className="w-5 text-right tabular-nums text-font-tertiary">{p.number}</span>
                      <span className="truncate">{p.jersey}</span>
                      <span className="ml-auto text-[10px] text-font-tertiary">{p.position}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function MatchCard({ match }: { match: WcMatch }) {
  const save = useWcSavePrediction();
  const { data: cfg } = useWcScoring();
  const pred = match.myPrediction;
  const [home, setHome] = useState(pred?.homeScore ?? 0);
  const [away, setAway] = useState(pred?.awayScore ?? 0);
  const [scorerId, setScorerId] = useState<string | null>(pred?.scorerId ?? null);
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const tz = useViewerTimezone();
  const k = formatKickoff(match.kickoff, tz);
  const finished = match.status === 'FINISHED';
  const live = match.status === 'LIVE';
  const editable = !match.locked && !finished && !live && !!match.homeTeam && !!match.awayTeam;
  const hasTeams = !!match.homeTeam && !!match.awayTeam;
  // Etiqueta en vivo: fase (1er/2do tiempo, medio tiempo…) + minuto si lo hay.
  const liveLabel = !live ? null : buildLiveLabel(match.liveStatus, match.liveMinute);

  const now = useNow();
  const countdown = editable ? countdownLabel(match.kickoff, now) : null;
  const maxPts = cfg
    ? Math.round((cfg.pointsExact + cfg.pointsScorer) * phaseMult(cfg, match.phase))
    : null;

  const dirty =
    home !== (pred?.homeScore ?? 0) ||
    away !== (pred?.awayScore ?? 0) ||
    scorerId !== (pred?.scorerId ?? null) ||
    !pred;

  const onSave = () => {
    setSaveError(null);
    save.mutate(
      { matchId: match.id, homeScore: home, awayScore: away, scorerId },
      {
        onSuccess: () => {
          setJustSaved(true);
          setTimeout(() => setJustSaved(false), 1800);
        },
        onError: (err: any) => {
          // El servidor es la barrera real: si el partido ya inició devuelve 409
          // aunque la app llevara rato abierta. Mostramos el motivo y refrescamos
          // la tarjeta para que pase a estado "cerrado".
          const msg =
            err?.response?.data?.error?.message ??
            'No se pudo guardar tu pronóstico. Refresca e inténtalo de nuevo.';
          setSaveError(msg);
        },
      },
    );
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-white p-4 shadow-sm transition',
        live
          ? 'border-error/40 ring-1 ring-error/30'
          : finished
            ? 'border-border'
            : 'border-border hover:border-primary/40 hover:shadow-md',
      )}
    >
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[11px] text-font-tertiary">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="rounded bg-primary-subtle px-1.5 py-0.5 font-semibold text-primary">
            {PHASE_SHORT[match.phase]}
            {match.group ? ` · ${match.group}` : ''}
          </span>
          <span className="truncate font-medium capitalize">
            {k.weekday}, {k.date} · {k.time}
          </span>
          {countdown && (
            <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 font-semibold text-warning">
              <Clock size={10} /> {countdown}
            </span>
          )}
        </span>
        <span className="flex flex-shrink-0 items-center gap-1.5">
          <span className="hidden items-center gap-1 truncate sm:flex">
            <MapPin size={11} /> {match.city}
          </span>
          {live && (
            <span className="flex items-center gap-1 rounded-full bg-error px-2 py-0.5 font-bold uppercase tracking-wide text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {liveLabel}
            </span>
          )}
          {editable &&
            (pred ? (
              <span className="rounded-full bg-accent-light px-2 py-0.5 font-bold uppercase tracking-wide text-accent">
                Predicho
              </span>
            ) : (
              <span className="rounded-full bg-warning/10 px-2 py-0.5 font-bold uppercase tracking-wide text-warning">
                Pendiente
              </span>
            ))}
        </span>
      </div>

      {/* Equipos + marcador — en móvil bandera arriba y nombre abajo (columnas) */}
      <div className="flex items-start gap-1.5 sm:items-center sm:gap-2">
        {/* Local */}
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1 sm:flex-row sm:gap-2">
          {match.homeTeam ? (
            <Flag iso2={match.homeTeam.iso2} className="h-6 w-9 flex-shrink-0 sm:h-7 sm:w-10" />
          ) : (
            <Flag className="h-6 w-9 flex-shrink-0 sm:h-7 sm:w-10" />
          )}
          <span className="min-w-0 max-w-full text-center text-[11px] font-semibold leading-tight text-font-primary sm:truncate sm:text-left sm:text-sm">
            {match.homeTeam ? match.homeTeam.nameEs : slotLabel(match.homeSlot)}
          </span>
        </div>

        {/* Centro: resultado / predicción */}
        <div className="flex flex-shrink-0 items-center gap-2 px-1">
          {editable ? (
            <>
              <Stepper value={home} onChange={setHome} />
              <span className="text-lg font-bold text-font-tertiary">:</span>
              <Stepper value={away} onChange={setAway} />
            </>
          ) : (
            <div className="flex flex-col items-center">
              {finished ? (
                <div className="flex items-center gap-2 text-2xl font-extrabold tabular-nums text-font-primary">
                  <span>{match.homeScore}</span>
                  <span className="text-font-tertiary">:</span>
                  <span>{match.awayScore}</span>
                </div>
              ) : live ? (
                <div className="flex items-center gap-2 text-2xl font-extrabold tabular-nums text-error">
                  <span>{match.homeScore ?? 0}</span>
                  <span className="text-error/50">:</span>
                  <span>{match.awayScore ?? 0}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xl font-bold tabular-nums text-font-tertiary">
                  <span>{pred?.homeScore ?? '–'}</span>
                  <span>:</span>
                  <span>{pred?.awayScore ?? '–'}</span>
                </div>
              )}
              {finished && match.homePens != null && match.awayPens != null && (
                <span className="text-[10px] font-semibold text-font-tertiary">
                  (pen. {match.homePens}-{match.awayPens})
                </span>
              )}
              {finished && match.reg90Home != null && match.reg90Away != null && (
                <span className="mt-0.5 text-center text-[10px] font-semibold leading-tight text-font-tertiary">
                  se calificó con el {match.reg90Home}-{match.reg90Away} del min. 90
                </span>
              )}
              {match.locked && !finished && !live && (
                <span className="mt-0.5 flex items-center gap-1 text-[10px] text-font-tertiary">
                  {pred ? (
                    'tu pronóstico'
                  ) : (
                    <>
                      <Lock size={9} /> cerrado
                    </>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Visitante */}
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1 sm:flex-row-reverse sm:gap-2">
          {match.awayTeam ? (
            <Flag iso2={match.awayTeam.iso2} className="h-6 w-9 flex-shrink-0 sm:h-7 sm:w-10" />
          ) : (
            <Flag className="h-6 w-9 flex-shrink-0 sm:h-7 sm:w-10" />
          )}
          <span className="min-w-0 max-w-full text-center text-[11px] font-semibold leading-tight text-font-primary sm:truncate sm:text-right sm:text-sm">
            {match.awayTeam ? match.awayTeam.nameEs : slotLabel(match.awaySlot)}
          </span>
        </div>
      </div>

      {/* Goleador (sólo editable) */}
      {editable && (
        <ScorerPicker
          match={match}
          value={scorerId}
          onChange={setScorerId}
          fallbackJersey={pred?.scorerJersey}
        />
      )}

      {/* Goleador elegido (visible también con el partido cerrado/finalizado) */}
      {!editable && pred?.scorerId && pred.scorerJersey && (
        <div className="mt-3 flex items-center gap-1.5 border-t border-border-light pt-3 text-xs">
          <Goal size={13} className="flex-shrink-0 text-primary" />
          <span className="text-font-secondary">Tu goleador:</span>
          <span className="font-semibold text-font-primary">{pred.scorerJersey}</span>
          {finished &&
            (match.scorerIds.includes(pred.scorerId) ? (
              <span className="ml-auto flex items-center gap-1 font-semibold text-accent">
                <Check size={12} /> anotó
              </span>
            ) : (
              <span className="ml-auto text-font-tertiary">no anotó</span>
            ))}
        </div>
      )}

      {/* Acciones / estado */}
      <div className="mt-3 flex items-center justify-between gap-2">
        {finished ? (
          <div className="flex w-full items-center justify-between text-xs">
            <span className="text-font-secondary">
              {pred ? (
                <>
                  Tu pronóstico:{' '}
                  <span className="font-semibold text-font-primary">
                    {pred.homeScore}-{pred.awayScore}
                  </span>
                </>
              ) : (
                <span className="text-font-tertiary">No pronosticaste</span>
              )}
            </span>
            {pred && (
              <span
                className={cn(
                  'flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold',
                  pred.points > 0
                    ? 'bg-accent-light text-accent'
                    : 'bg-bg text-font-tertiary',
                )}
              >
                <Trophy size={12} /> {pred.points} pts
              </span>
            )}
          </div>
        ) : editable ? (
          <>
            <span className="text-xs text-font-tertiary">
              {justSaved ? (
                <span className="flex items-center gap-1 font-semibold text-accent">
                  <Check size={13} /> Guardado
                </span>
              ) : (
                <>
                  {pred ? 'Editable hasta el inicio' : 'Aún no pronosticas'}
                  {maxPts != null && (
                    <span className="text-primary"> · Máximo posible: {maxPts} pts</span>
                  )}
                </>
              )}
            </span>
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-md px-4 text-sm font-semibold transition',
                dirty && !save.isPending
                  ? 'bg-primary text-white shadow-sm hover:bg-primary-dark'
                  : 'cursor-not-allowed bg-bg text-font-tertiary',
              )}
            >
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {pred ? 'Actualizar' : 'Guardar'}
            </button>
          </>
        ) : live ? (
          <div className="flex w-full items-center justify-between text-xs">
            <span className="text-font-secondary">
              {pred ? (
                <>
                  Tu pronóstico:{' '}
                  <span className="font-semibold text-font-primary">
                    {pred.homeScore}-{pred.awayScore}
                  </span>
                </>
              ) : (
                <span className="text-font-tertiary">No pronosticaste</span>
              )}
            </span>
            <span className="flex items-center gap-1 font-semibold text-error">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-error" /> Marcador en vivo
            </span>
          </div>
        ) : (
          <span className="flex items-center gap-1 text-xs text-font-tertiary">
            <Lock size={11} />
            {hasTeams ? 'Pronóstico cerrado' : 'Equipos por definir'}
          </span>
        )}
      </div>

      {saveError && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-error">
          <Lock size={13} className="mt-0.5 flex-shrink-0" /> {saveError}
        </div>
      )}
    </div>
  );
}
